import * as React from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Divider,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import Grid from '@mui/system/Unstable_Grid';
import {
  ArrowLeft as ArrowLeftIcon,
  Bank as BankIcon,
  CalendarBlank as CalendarBlankIcon,
  CheckCircle as CheckCircleIcon,
  Clock as ClockIcon,
  CurrencyDollar as CurrencyDollarIcon,
  PencilSimple as PencilSimpleIcon,
  Trash as TrashIcon,
  Users as UsersIcon,
  Wine as WineIcon,
  XCircle as XCircleIcon,
} from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirm } from 'material-ui-confirm';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';

import type { Amenity, AvailabilitySchedule } from '@/types/v2/amenity';
import { deleteAmenity, getAmenity } from '@/api/amenities';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { paths } from '@/paths';
import { useUser } from '@/hooks/v2/use-user';
import { useDialog } from '@/hooks/v2/use-dialog';
import { toast } from '@/components/core/v2/toaster';
import { PropertyItem } from '@/components/core/v2/property-item';
import { PropertyList } from '@/components/core/v2/property-list';
import { hasAnyAccountRoles, hasCommunityRolesV2 } from '@/utils/has-roles';

import { EditAmenityDialog } from '../components/edit-amenity-dialog';

const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

function formatTime(time: string): string {
  return dayjs(`2000-01-01 ${time}`).format('h:mm A');
}

function formatCurrency(amount: number | string | null | undefined): string {
  const n = typeof amount === 'number' ? amount : Number(amount);
  return n > 0 && !Number.isNaN(n) ? `$${n.toFixed(2)}` : '—';
}

export function AmenityDetailPage(): React.JSX.Element {
  const { amenityId } = useParams<{ amenityId: string }>();
  const { account, community } = useUser();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const editDialog = useDialog();
  const canManageAmenities = hasCommunityRolesV2(community, [
    COMMUNITY_ROLES.COMMUNITY_MANAGER,
    COMMUNITY_ROLES.BOARD_MEMBER,
  ]) || hasAnyAccountRoles(account!, [
    ACCOUNT_ROLES.ACCOUNT_OWNER,
    ACCOUNT_ROLES.ACCOUNT_ADMIN,
    ACCOUNT_ROLES.ACCOUNT_MANAGER,
  ]);

  const { data: amenity, isLoading, error, refetch } = useQuery<Amenity>({
    queryKey: ['amenity', amenityId],
    queryFn: () => getAmenity(community?.id || '', amenityId!),
    enabled: !!community?.id && !!amenityId,
    retry: false,
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteAmenity(community?.id || '', amenityId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['amenities', community?.id] });
      toast.success('Amenity deleted');
      navigate(paths.settings.community.amenities.index);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to delete amenity');
    },
  });

  const handleDelete = async () => {
    try {
      await confirm({
        title: 'Delete Amenity',
        description: `Are you sure you want to permanently delete "${amenity?.name}"? This will also remove all associated reservations.`,
        confirmationText: 'Delete',
        confirmationButtonProps: { color: 'error', variant: 'contained' },
        cancellationButtonProps: { variant: 'outlined' },
      });
      deleteMutation.mutate();
    } catch {
      // cancelled
    }
  };

  const handleEditSuccess = React.useCallback(async () => {
    await refetch();
    editDialog.handleClose();
  }, [refetch, editDialog]);

  if (!isLoading && (error?.message === 'Amenity not found' || (!amenity && !isLoading))) {
    return (
      <Box sx={{ py: 10, textAlign: 'center' }}>
        <BankIcon color={theme.palette.text.disabled} size={64} style={{ marginBottom: 24 }} />
        <Typography gutterBottom sx={{ fontWeight: 600 }} variant="h5">Amenity not found</Typography>
        <Typography color="text.secondary" sx={{ mb: 4 }} variant="body2">
          This amenity doesn't exist or has been removed.
        </Typography>
        <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'center' }}>
          <Button onClick={() => window.history.back()} variant="outlined">Go Back</Button>
          <Button onClick={() => navigate(paths.settings.community.amenities.index)} variant="contained">All Amenities</Button>
        </Stack>
      </Box>
    );
  }

  return (
    <>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        {isLoading ? (
          <Stack spacing={1.5}>
            <Skeleton height={18} width={180} variant="text" />
            <Skeleton height={36} width={280} variant="text" />
            <Skeleton height={18} width={240} variant="text" />
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Breadcrumbs
              separator={<Typography sx={{ color: 'text.disabled', fontSize: '0.8125rem', mx: -0.5 }}>/</Typography>}
              sx={{ fontSize: '0.8125rem' }}
            >
              <Link
                component="button"
                onClick={() => navigate(paths.settings.community.amenities.index)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'text.secondary',
                  textDecoration: 'none',
                  fontSize: 'inherit',
                  '&:hover': { color: 'text.primary' },
                }}
                underline="none"
              >
                <ArrowLeftIcon size={13} />
                Amenities
              </Link>
              <Typography color="text.primary" sx={{ fontSize: 'inherit', fontWeight: 500 }}>
                {amenity?.name}
              </Typography>
            </Breadcrumbs>

            <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
              <Stack spacing={1}>
                <Typography sx={{ fontWeight: 700, lineHeight: 1.25 }} variant="h4">
                  {amenity?.name}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    icon={amenity?.is_active ? <CheckCircleIcon size={14} /> : <XCircleIcon size={14} />}
                    label={amenity?.is_active ? 'Active' : 'Inactive'}
                    size="small"
                    color={amenity?.is_active ? 'success' : 'default'}
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                  {amenity?.capacity && (
                    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: 'text.secondary' }}>
                      <UsersIcon size={13} />
                      <Typography variant="caption">Capacity: {amenity.capacity}</Typography>
                    </Stack>
                  )}
                  {amenity?.created_at && (
                    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: 'text.secondary' }}>
                      <CalendarBlankIcon size={13} />
                      <Typography variant="caption">
                        Created {dayjs(amenity.created_at).format('MMM D, YYYY')}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Stack>

              {canManageAmenities ? (
                <Stack direction="row" spacing={1}>
                  <Button
                    onClick={editDialog.handleOpen}
                    size="small"
                    startIcon={<PencilSimpleIcon size={16} />}
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  >
                    Edit
                  </Button>
                  <Button
                    onClick={handleDelete}
                    size="small"
                    color="error"
                    startIcon={<TrashIcon size={16} />}
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  >
                    Delete
                  </Button>
                </Stack>
              ) : null}
            </Stack>
          </Stack>
        )}
      </Box>

      {/* Body */}
      <Grid container spacing={3}>
        {/* Left column */}
        <Grid md={8} xs={12}>
          <Stack spacing={3}>
            {/* Description */}
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ p: { xs: 2, md: 3 }, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Description</Typography>
              </Box>
              <Box sx={{ p: { xs: 2, md: 3 } }}>
                {isLoading ? (
                  <>
                    <Skeleton height={20} variant="text" />
                    <Skeleton height={20} width="80%" variant="text" />
                  </>
                ) : amenity?.description ? (
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {amenity.description}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                    No description provided
                  </Typography>
                )}
              </Box>
            </Paper>

            {/* Availability */}
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ p: { xs: 2, md: 3 }, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Availability</Typography>
              </Box>
              <Box sx={{ p: { xs: 2, md: 3 } }}>
                {isLoading ? (
                  <Skeleton height={80} variant="rectangular" sx={{ borderRadius: 1 }} />
                ) : (
                  <Stack spacing={2}>
                    {/* Schedule */}
                    {amenity?.availability_schedule && Object.keys(amenity.availability_schedule).length > 0 ? (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Weekly Schedule</Typography>
                        <Stack spacing={0.5}>
                          {DAY_KEYS.map((key, idx) => {
                            const schedule = amenity.availability_schedule?.[key as keyof AvailabilitySchedule];
                            return schedule ? (
                              <Stack key={key} direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, width: 80 }}>
                                  {DAY_LABELS[idx]}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={`${formatTime(schedule.start)} — ${formatTime(schedule.end)}`}
                                  variant="outlined"
                                  sx={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '0.75rem' }}
                                />
                              </Stack>
                            ) : null;
                          })}
                        </Stack>
                      </Box>
                    ) : amenity?.available_days && amenity.available_days.length > 0 ? (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Available Days</Typography>
                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                          {amenity.available_days.map((d) => (
                            <Chip key={d} label={DAY_LABELS[d]?.slice(0, 3)} size="small" variant="outlined" />
                          ))}
                        </Stack>
                        {amenity.available_start_time && amenity.available_end_time && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {formatTime(amenity.available_start_time)} — {formatTime(amenity.available_end_time)}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Available anytime (no restrictions configured)
                      </Typography>
                    )}

                    {/* Seasonal */}
                    {(amenity?.seasonal_start_date || amenity?.seasonal_end_date) && (
                      <>
                        <Divider />
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Seasonal Availability</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {amenity.seasonal_start_date ? dayjs(amenity.seasonal_start_date).format('MMM D') : 'Start of year'}
                            {' — '}
                            {amenity.seasonal_end_date ? dayjs(amenity.seasonal_end_date).format('MMM D') : 'End of year'}
                          </Typography>
                        </Box>
                      </>
                    )}

                    {/* Time blocks */}
                    {amenity?.time_block_duration && (
                      <>
                        <Divider />
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Time Blocks</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Fixed {amenity.time_block_duration}-minute blocks
                          </Typography>
                        </Box>
                      </>
                    )}

                    {/* Duration limits */}
                    {(amenity?.reservation_min_duration || amenity?.reservation_max_duration) && (
                      <>
                        <Divider />
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Duration Limits</Typography>
                          <Stack direction="row" spacing={2}>
                            {amenity.reservation_min_duration && (
                              <Typography variant="body2" color="text.secondary">
                                Min: {amenity.reservation_min_duration} min
                              </Typography>
                            )}
                            {amenity.reservation_max_duration && (
                              <Typography variant="body2" color="text.secondary">
                                Max: {amenity.reservation_max_duration} min
                              </Typography>
                            )}
                          </Stack>
                        </Box>
                      </>
                    )}
                  </Stack>
                )}
              </Box>
            </Paper>

            {/* Rules */}
            {amenity?.rules && (
              <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ p: { xs: 2, md: 3 }, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Rules & Guidelines</Typography>
                </Box>
                <Box sx={{ p: { xs: 2, md: 3 } }}>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {amenity.rules}
                  </Typography>
                </Box>
              </Paper>
            )}
          </Stack>
        </Grid>

        {/* Right column (sidebar) */}
        <Grid md={4} xs={12}>
          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              position: { md: 'sticky' },
              top: { md: 24 },
            }}
          >
            {/* Fees */}
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>Fees & Deposits</Typography>
              {isLoading ? (
                <Skeleton height={60} variant="rectangular" sx={{ borderRadius: 1 }} />
              ) : (
                <Stack spacing={1.5}>
                  <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Total Fee</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(amenity?.total_fee_amount)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Deposit</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(amenity?.deposit_amount)}
                    </Typography>
                  </Stack>
                  {amenity?.fee_description && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                      {amenity.fee_description}
                    </Typography>
                  )}
                </Stack>
              )}
            </Box>

            <Divider />

            {/* Settings */}
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>Settings</Typography>
              {isLoading ? (
                <Skeleton height={120} variant="rectangular" sx={{ borderRadius: 1 }} />
              ) : (
                <PropertyList orientation="vertical" sx={{ '--PropertyItem-padding': '6px 0' }}>
                  <PropertyItem
                    name="Requires Approval"
                    value={
                      <Chip
                        label={amenity?.requires_approval ? 'Yes' : 'No'}
                        size="small"
                        color={amenity?.requires_approval ? 'warning' : 'info'}
                        variant="outlined"
                      />
                    }
                  />
                  <PropertyItem
                    name="Guest Count Required"
                    value={amenity?.guest_count_required ? 'Yes' : 'No'}
                  />
                  {amenity?.max_guests && (
                    <PropertyItem name="Max Guests" value={String(amenity.max_guests)} />
                  )}
                  <PropertyItem
                    name="Alcohol Question"
                    value={amenity?.alcohol_question_enabled ? 'Enabled' : 'Disabled'}
                  />
                </PropertyList>
              )}
            </Box>

            <Divider />

            {/* Anti-hogging */}
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>Booking Limits</Typography>
              {isLoading ? (
                <Skeleton height={60} variant="rectangular" sx={{ borderRadius: 1 }} />
              ) : (
                <PropertyList orientation="vertical" sx={{ '--PropertyItem-padding': '6px 0' }}>
                  <PropertyItem
                    name="Per Week"
                    value={amenity?.max_bookings_per_week ? String(amenity.max_bookings_per_week) : 'Unlimited'}
                  />
                  <PropertyItem
                    name="Per Month"
                    value={amenity?.max_bookings_per_month ? String(amenity.max_bookings_per_month) : 'Unlimited'}
                  />
                  <PropertyItem
                    name="Min Days Between"
                    value={amenity?.min_days_between_bookings ? `${amenity.min_days_between_bookings} days` : 'None'}
                  />
                </PropertyList>
              )}
            </Box>

            {/* Blackout dates */}
            {amenity?.blackout_dates && amenity.blackout_dates.length > 0 && (
              <>
                <Divider />
                <Box sx={{ p: { xs: 2, md: 3 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>Blackout Dates</Typography>
                  <Stack spacing={1}>
                    {amenity.blackout_dates.map((bd) => (
                      <Box key={bd.id} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {dayjs(bd.start_date).format('MMM D')} — {dayjs(bd.end_date).format('MMM D, YYYY')}
                        </Typography>
                        {bd.reason && (
                          <Typography variant="caption" color="text.secondary">{bd.reason}</Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Edit dialog */}
      {amenity && (
        <EditAmenityDialog
          open={editDialog.open}
          amenity={amenity}
          amenityId={amenityId}
          onClose={editDialog.handleClose}
          onSuccess={handleEditSuccess}
        />
      )}
    </>
  );
}
